import { supabase, SUPABASE_URL } from '@/integrations/supabase/client';
import { log } from '@/lib/logger';
import { dedupedRefreshSession } from '@/lib/getRefreshedToken';
import {
  ENRICHMENT_DISABLED_IN_DEMO,
  EnrichmentBlockedError,
  showEnrichmentBlockedToast,
} from '@/lib/enrichmentBlocked';
import { isViewOnlySessionActive, VIEW_ONLY_MESSAGE } from '@/lib/viewOnlySession';

interface InvokeOptions {
  body?: Record<string, unknown>;
  headers?: Record<string, string>;
  method?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE';
  signal?: AbortSignal;
  /** Skip the automatic token refresh before invocation. Use for unauthenticated calls. */
  skipTokenRefresh?: boolean;
  /** Timeout in milliseconds. Defaults to 30000 (30s). Set 0 to disable. */
  timeoutMs?: number;
  /** Max retry attempts for transient failures. Defaults to 0 (no retry). */
  maxRetries?: number;
  /** Only read-only requests may replay after an uncertain transport failure. */
  retrySafety?: 'read_only';
  /** Called with the generated request ID before the request is sent. Useful for operation tracking. */
  onRequestId?: (requestId: string) => void;
}

/** Potentially transient responses; these do not prove a mutation was not executed. */
const RETRYABLE_STATUS_CODES = new Set([429, 502, 503, 504]);

/** Delay before retry with exponential backoff + jitter.
 *  429 rate limits use a longer base delay (3s instead of 1s). */
/** Functions a view-only member may call: they read and spend nothing. Mirrors `viewOnly.allowed` on the server. */
export const VIEW_ONLY_EDGE_FUNCTIONS: ReadonlySet<string> = new Set([
  'warm-paths',
  'company-suggest',
  'check-subscription',
  'bug-report-submitted-notify',
  // Mixed functions: only their read actions are allowed by the server.
  'manage-data-suggestions',
  'reasoning-agent',
  'deal-room',
  // Public recipients authorize with their personal invitation, independently
  // of any unrelated workspace role cached in this browser.
  'deal-room-public',
]);

/** Thrown instead of calling a function a view-only member may not use. */
export class ViewOnlyError extends Error {
  readonly code = 'view_only';
  constructor() {
    super(VIEW_ONLY_MESSAGE);
    this.name = 'ViewOnlyError';
  }
}

function retryDelay(attempt: number, status?: number): number {
  const baseMultiplier = status === 429 ? 3000 : 1000;
  const base = Math.min(baseMultiplier * Math.pow(2, attempt), 15000);
  const jitter = Math.random() * 500;
  return base + jitter;
}

/**
 * Potentially transient failures. A lost response can follow successful execution;
 * classification alone never authorises replay.
 *
 * "Failed to send a request to the Edge Function" is what supabase-js throws
 * when the underlying fetch rejects: a dropped connection, a laptop waking from
 * sleep, a blocked request. It was missing from this list, so opt-in retry
 * could never recover from the most common transport failure we actually see
 * (NYNCH-5N — 37 events across 3 users in 13 days).
 *
 * Only an explicitly read-only caller can opt into automatic replay.
 */
export const RETRYABLE_TRANSPORT_PATTERNS = [
  'fetch failed',
  'failed to fetch',
  'failed to send a request',
  'network',
  'timeout',
  'timed out',
  'load failed',
  'connection closed',
] as const;

/** Check if an error is retryable (network errors or gateway failures) */
export function isRetryableError(error: unknown, status?: number): boolean {
  if (status && RETRYABLE_STATUS_CODES.has(status)) return true;

  const message = error instanceof Error
    ? error.message
    : (error && typeof error === 'object' && 'message' in error
      ? String((error as { message: unknown }).message)
      : '');
  if (!message) return false;

  const msg = message.toLowerCase();
  return RETRYABLE_TRANSPORT_PATTERNS.some(pattern => msg.includes(pattern));
}

/**
 * Retry count for callers that also establish read-only retry safety.
 */
export const USER_FACING_EDGE_RETRIES = 1;

/**
 * Callers already toast. Potentially transient failures are warnings; execution
 * may still have happened, so this logging choice does not authorise replay.
 */
export function logHandledEdgeFailure(label: string, error: unknown): void {
  if (isRetryableError(error)) {
    log.warn(label, error);
    return;
  }
  log.error(label, error);
}

/**
 * Invoke a Supabase Edge Function with automatic token refresh, retry, and proper error extraction.
 *
 * - Explicitly passes a fresh access token in the Authorization header (prevents stale-token 401s)
 * - Proactively refreshes tokens within 60s of expiry
 * - Automatically retries once on 401 "Invalid JWT" with a forced token refresh
 * - Reads the response body on error to surface the actual error message
 * - Adds configurable timeout to prevent indefinite hangs
 * - Adds optional retry with exponential backoff for transient failures
 * - Includes X-Request-ID header for request correlation
 */
export async function invokeEdgeFunction<T>(
  functionName: string,
  options: InvokeOptions = {}
): Promise<T> {
  // A view-only member's calls to anything but the read-only functions are
  // refused by the server (403 view_only). Answer locally instead, so opening
  // a record never fires paid AI or website watches for them, and nothing is
  // sent just to be refused.
  if (isViewOnlySessionActive() && !VIEW_ONLY_EDGE_FUNCTIONS.has(functionName)) {
    throw new ViewOnlyError();
  }
  const {
    body,
    headers: userHeaders,
    method,
    signal,
    skipTokenRefresh,
    timeoutMs = 30000,
    maxRetries: requestedRetries = 0,
    retrySafety,
    onRequestId } = options;
  const maxRetries = retrySafety === 'read_only' ? requestedRetries : 0;

  // Generate a unique request ID for correlation between frontend and edge function logs
  const requestId = crypto.randomUUID();
  onRequestId?.(requestId);
  const mergedHeaders: Record<string, string> = {
    'X-Request-ID': requestId,
    ...userHeaders };

  // Explicitly pass a fresh access token in the Authorization header.
  // The Supabase client's internal token management can use stale tokens,
  // causing "Invalid JWT" from the gateway on verify_jwt=true functions.
  if (!skipTokenRefresh) {
    const freshToken = await getFreshAccessToken(functionName);
    mergedHeaders['Authorization'] = `Bearer ${freshToken}`;
  }

  // Total attempts = 1 + maxRetries + 1 possible 401 retry
  let lastError: Error | undefined;
  let lastResponseStatus: number | undefined;
  let did401Retry = false;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    signal?.throwIfAborted();
    // Wait before retry (skip delay on first attempt)
    if (attempt > 0) {
      const delay = retryDelay(attempt - 1, lastResponseStatus);
      log.info(`Retrying ${functionName} (attempt ${attempt + 1}/${maxRetries + 1}) after ${Math.round(delay)}ms`, { requestId });
      await new Promise(resolve => setTimeout(resolve, delay));
      signal?.throwIfAborted();
    }

    // Create a timeout signal if none provided and timeout is enabled
    let effectiveSignal = signal;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (!signal && timeoutMs > 0) {
      const controller = new AbortController();
      effectiveSignal = controller.signal;
      timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    }

    try {
      const { data, error, response } = await supabase.functions.invoke<T>(
        functionName,
        { body, headers: mergedHeaders, method, signal: effectiveSignal }
      );

      if (error) {
        let errorDetail = `${functionName} request failed`;
        let responseStatus: number | undefined;

        // Supabase functions-js v2.95+ sets error.context to the raw Response
        // object for FunctionsHttpError/FunctionsRelayError. The body has NOT
        // been consumed at this point, so we can read it to extract the actual
        // error message from the edge function's JSON response.
        const errorContext = (error as Record<string, unknown>).context;
        const contextIsResponse = errorContext != null &&
          typeof (errorContext as Response).text === 'function' &&
          typeof (errorContext as Response).status === 'number';

        // Use the Response from error.context (preferred) or the response return value
        const errorResponse = contextIsResponse
          ? errorContext as Response
          : response;

        if (errorResponse) {
          responseStatus = errorResponse.status;
          lastResponseStatus = responseStatus;
          try {
            const bodyText = await errorResponse.text();
            try {
              const responseBody = JSON.parse(bodyText);
              // responseBody.error can be a string (legacy callers) OR an object
              // like { code, message } from edge functions that return structured
              // errors (mcp-oauth-init etc). Coerce to a readable string.
              const rawError: unknown = responseBody?.error;
              let parsedError: string | undefined;
              if (typeof rawError === 'string') {
                parsedError = rawError;
              } else if (rawError && typeof rawError === 'object') {
                const errObj = rawError as { message?: unknown; code?: unknown };
                if (typeof errObj.message === 'string') {
                  parsedError = errObj.message;
                } else if (typeof errObj.code === 'string') {
                  parsedError = errObj.code;
                } else {
                  parsedError = JSON.stringify(rawError);
                }
              }
              const parsedMessage = typeof responseBody?.message === 'string' ? responseBody.message : undefined;
              errorDetail = parsedError || parsedMessage || errorDetail;
            } catch {
              if (bodyText) {
                errorDetail = bodyText.substring(0, 200);
              }
            }
          } catch {
            // Body unreadable — keep default errorDetail
          }

          // Auth errors (401, JWT, session expired) are expected during login
          // hydration — downgrade to warn so they don't flood Sentry.
          const isAuthStatus = responseStatus === 401 || responseStatus === 403;
          const detailLower = errorDetail.toLowerCase();
          const isAuthMessage = detailLower.includes('jwt') || detailLower.includes('not authenticated')
            || detailLower.includes('session expired') || detailLower.includes('sign in')
            || detailLower.includes('unauthorized');
          if (isAuthStatus || isAuthMessage) {
            log.warn(`Edge function auth error: ${functionName}`, { errorDetail, requestId, status: responseStatus });
          } else {
            // Warn, not error — the caller decides severity. Logging error here
            // AND in the calling hook creates duplicate Sentry events for every
            // handled failure (e.g., synthesize-contact-profile, discover-warm-targets).
            log.warn(`Edge function error: ${functionName}`, { errorDetail, requestId, status: responseStatus });
          }
        } else {
          // No response object — relay error or network failure
          errorDetail = error.message || errorDetail;
          const msgLower = errorDetail.toLowerCase();
          const isRelayAuth = msgLower.includes('jwt') || msgLower.includes('not authenticated')
            || msgLower.includes('unauthorized') || msgLower.includes('boot error')
            || msgLower.includes('non-2xx');
          if (isRelayAuth) {
            log.warn(`Edge function relay error: ${functionName}`, { message: errorDetail, requestId });
          } else {
            // Warn, not error — same rationale: callers handle these and log
            // their own errors if needed. Prevents duplicate Sentry events.
            log.warn(`Edge function invocation error: ${functionName}`, { message: errorDetail, requestId });
          }
        }

        // On 401 "Invalid JWT", force-refresh and retry once.
        // Check both response status AND error message — relay errors from the
        // Supabase gateway may not include a response object, only an error message.
        const is401 = responseStatus === 401 ||
          (!responseStatus && errorDetail.toLowerCase().includes('invalid jwt'));
        if (is401 && !did401Retry && !skipTokenRefresh) {
          did401Retry = true;
          log.info(`401 on ${functionName}, forcing token refresh and retrying`, { requestId });
          try {
            const freshToken = await forceTokenRefresh(functionName);
            mergedHeaders['Authorization'] = `Bearer ${freshToken}`;
            // Don't increment attempt counter — this is a bonus retry
            attempt--;
            continue;
          } catch {
            // Refresh failed, fall through to throw
          }
        }

        // Demo-account enrichment block: swap the raw sentinel for a friendly
        // toast and a typed error. Hooks that don't add special handling will
        // still surface the friendly message rather than a cryptic code.
        if (errorDetail === ENRICHMENT_DISABLED_IN_DEMO) {
          showEnrichmentBlockedToast();
          throw new EnrichmentBlockedError();
        }

        lastError = new Error(isRetryableError(error, responseStatus) && retrySafety !== 'read_only'
          ? `${errorDetail}. Execution could not be confirmed. Check the saved result before retrying; the previous request may have completed.`
          : errorDetail);

        // Check if this error is retryable
        if (attempt < maxRetries && isRetryableError(error, responseStatus)) {
          continue; // Retry
        }

        throw lastError;
      }

      return data as T;
    } catch (thrown) {
      if (timeoutId) clearTimeout(timeoutId);

      // If it's our own Error from above, re-throw unless retryable
      if (thrown === lastError) {
        throw thrown;
      }

      // Network/timeout errors
      const err = thrown instanceof Error ? thrown : new Error(String(thrown));
      lastError = err;

      if (attempt < maxRetries && isRetryableError(err)) {
        continue; // Retry
      }

      if (isRetryableError(err) && retrySafety !== 'read_only') {
        throw new Error(`${err.message}. Execution could not be confirmed. Check the saved result before retrying; the previous request may have completed.`);
      }
      throw err;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  // Should never reach here, but TypeScript needs it
  throw lastError || new Error(`${functionName} failed after ${maxRetries + 1} attempts`);
}

/**
 * Invoke a task on the consolidated `ai-task` edge dispatcher.
 *
 * Many small AI features that used to each be their own edge function now live
 * as tasks behind one dispatcher (to stay under Supabase's 500-function cap).
 * Callers name the task instead of a function: `invokeAITask('improve-text', { text, action })`.
 * The dispatcher routes by `body.task` and preserves each task's original auth,
 * rate limit, and behaviour.
 */
export function invokeAITask<T>(
  task: string,
  body: Record<string, unknown> = {},
  options: Omit<InvokeOptions, 'body'> = {}
): Promise<T> {
  return invokeEdgeFunction<T>('ai-task', { ...options, body: { task, ...body } });
}

/**
 * Get a fresh access token from the cached session.
 *
 * Uses getSession() (local cache only — no server call) to read the current
 * access token. This avoids gotrue-js's getUser() which makes a server call
 * and nukes the local session from localStorage on any transient 401.
 *
 * Server-side token validation is redundant here because:
 * 1. Every edge function validates the token via withEdgeGuard
 * 2. invokeEdgeFunction already retries on 401 with forceTokenRefresh()
 *
 * Proactively refreshes tokens nearing expiry (< 60s) to minimize 401 retries.
 */
async function getFreshAccessToken(functionName: string): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) {
    // No cached session — try refreshing before giving up
    log.info(`No session found before ${functionName}, attempting refresh`);
    return dedupedRefreshSession();
  }

  // Proactively refresh if token expires within 60 seconds
  const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
  if (expiresAt > 0 && Date.now() > expiresAt - 60_000) {
    log.info(`Proactive token refresh before ${functionName} (expires in ${Math.round((expiresAt - Date.now()) / 1000)}s)`);
    return dedupedRefreshSession();
  }

  return session.access_token;
}

/**
 * Force a token refresh (used after receiving a 401 from the gateway).
 */
async function forceTokenRefresh(functionName: string): Promise<string> {
  try {
    return await dedupedRefreshSession();
  } catch {
    // Token refresh failure is expected during login hydration — downgrade to
    // warn so it doesn't flood Sentry. The caller will throw a user-facing error.
    log.warn(`Force refresh failed for ${functionName}`);
    throw new Error('Session expired. Please sign in again.');
  }
}

// ── Streaming Edge Function Invocation ──────────────────────────────────────

export interface ThinkingStep {
  step: string;
  detail?: string;
  durationMs?: number;
  /** Reasoning sentence supplied by the LLM (see consult_* tools). When set,
   *  the UI renders the step as a collapsible reasoning card. */
  reason?: string;
}

export interface StreamCallbacks {
  onThinkingSteps?: (steps: ThinkingStep[]) => void;
  onMetadata?: (metadata: Record<string, unknown>) => void;
  onChunk?: (text: string) => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Invoke a Supabase Edge Function that returns an SSE stream.
 *
 * - Same token refresh / 401 retry logic as `invokeEdgeFunction`
 * - Parses SSE events with `type: 'metadata'` and `type: 'chunk'`
 * - Calls the appropriate callback for each event type
 * - Sends `Accept: text/event-stream` header to signal streaming
 */
export async function invokeEdgeFunctionStream(
  functionName: string,
  options: {
    body?: Record<string, unknown>;
    signal?: AbortSignal;
    timeoutMs?: number;
  } & StreamCallbacks
): Promise<void> {
  const { body, signal, timeoutMs = 120000, onThinkingSteps, onMetadata, onChunk, onComplete, onError } = options;

  const requestId = crypto.randomUUID();
  const supabaseUrl = SUPABASE_URL;

  let accessToken: string;
  try {
    accessToken = await getFreshAccessToken(functionName);
  } catch (err) {
    onError?.(err instanceof Error ? err : new Error(String(err)));
    return;
  }

  // Timeout controller
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const controller = new AbortController();
  const effectiveSignal = signal || controller.signal;
  if (!signal && timeoutMs > 0) {
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  }

  const makeRequest = (token: string) =>
    fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Authorization': `Bearer ${token}`,
        'X-Request-ID': requestId },
      body: JSON.stringify(body),
      signal: effectiveSignal });

  try {
    let response = await makeRequest(accessToken);

    // Retry on 401
    if (response.status === 401) {
      log.info(`401 on ${functionName} stream, refreshing token and retrying`, { requestId });
      try {
        accessToken = await forceTokenRefresh(functionName);
        response = await makeRequest(accessToken);
      } catch {
        // Refresh failed — fall through to error handling
      }
    }

    if (!response.ok) {
      let errorDetail = `Stream error: ${response.status}`;
      try {
        const errorBody = await response.json();
        errorDetail = (errorBody as Record<string, string>)?.error
          || (errorBody as Record<string, string>)?.message
          || errorDetail;
      } catch {
        // Non-JSON error body
      }
      throw new Error(errorDetail);
    }

    if (!response.body) {
      throw new Error('No response body for stream');
    }

    // Parse SSE stream
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.trim() || line.startsWith(':')) continue;
        if (!line.startsWith('data: ')) continue;

        const data = line.slice(6).trim();
        if (data === '[DONE]') continue;

        try {
          const parsed = JSON.parse(data) as Record<string, unknown>;
          if (parsed.type === 'thinking_steps') {
            onThinkingSteps?.(parsed.steps as ThinkingStep[]);
          } else if (parsed.type === 'metadata') {
            onMetadata?.(parsed);
          } else if (parsed.type === 'chunk' && typeof parsed.content === 'string') {
            onChunk?.(parsed.content);
          } else if (parsed.type === 'error') {
            const message = typeof parsed.message === 'string' ? parsed.message : 'Stream error';
            onError?.(new Error(message));
            return;
          }
        } catch {
          // Skip invalid JSON
        }
      }
    }

    onComplete?.();
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    onError?.(error);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}
